import React from 'react';
import { Route, Switch, useRouteMatch, useParams, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import decode from 'jwt-decode';
import PrivateRoute from './PrivateRoute';
import { ADMIN } from './Common/constants';
import { getAuthToken, hasValidAppSession } from '../utils/authSession';
import { getPrimaryRole } from '../utils/rolePriority';

import SpaceListing from './SpaceListing/SpaceListing';
import HostOnBoarding from './HostOnBoarding/HostOnboarding';
import FeaturedSpaces from './Result/FeaturedSpaces';
import ReportContainer from './Reports/ReportContainer';
import HostReport from './Reports/HostReport/HostReport';
import SpaceContainer from './HostDashboard/SpaceContainer';
import CalendarWrapper from './HostDashboard/CalendarWrapper';
import ManageVisitsWrapper from './HostDashboard/ManageVisitsWrapper';
import UserManagement from './HostDashboard/UserManagement';
import AdminDashboardLayout from './Common/AdminDashboardLayout/AdminDashboardLayout';

const Home = React.lazy(() => import('./Home/Home'));
const Result = React.lazy(() => import('./Result/Result'));
const SearchLandingRedirect = React.lazy(() => import('./Result/SearchLandingRedirect'));
const NavBar = React.lazy(() => import('./Common/NavBar/NavBar'));
const Dialog = React.lazy(() => import('./Common/Dialog/Dialog'));
const Booking = React.lazy(() => import('./Booking/Booking'));

const Calendar = React.lazy(() => import('./HostDashboard/Calendar'));
const ManageVisits = React.lazy(() => import('./HostDashboard/ManageVisits'));

const Message = React.lazy(() => import('./Dashboard/Message'));
const Dashboard = React.lazy(() => import('./Dashboard/Dashboard'));
const MyBookings = React.lazy(() => import('./BookingCancelation/MyBookings'));
const loading = React.lazy(() => import('./Common/loading'));
const ProfileAndBooking = React.lazy(() => import('./SpaceProfile/ProfileAndBooking'));
const HelpAndSupport = React.lazy(() => import('./Static-pages/HelpAndSupport/HelpAndSupport'));
const AboutUs = React.lazy(() => import('./Static-pages/AboutUs/AboutUs'));
const TermsAndConditions = React.lazy(() => import('./Static-pages/TermAndConditions/TermsAndConditions'));
const ForgotPassword = React.lazy(() => import('./Common/NavBar/Login/forgot-password/ForgotPassword'));
const EmailConfirmation = React.lazy(() => import('./Account-Verification/Email-Verification/EmailConfirmation'));
const Faq = React.lazy(() => import('./Static-pages/Faq/Faq'));
const ReviewPage = React.lazy(() => import('./Home/Reviews/ReviewPage'));
const Blog = React.lazy(() => import('./Home/Blog/Blog'));
const BlogDetail = React.lazy(() => import('./Home/Blog/BlogDetail'));
const AdminDashboard = React.lazy(() => import('./AdminDashboard/AdminDashboard'));
const PaidPromotions = React.lazy(() => import('./HostDashboard/PaidPromotions/PaidPromotions'));
const ImageEnhance = React.lazy(() => import('./ImageEnhance/ImageEnhance'));

const AdminRoute = ({ component: Component, ...rest }) => {
  const authDetails = useSelector((state) => state?.auth?.loginDetails?.data?.token || null);
  const { country } = useParams();
  
  // Check both Redux state and cookies for token
  const tokenFromCookie = hasValidAppSession() ? getAuthToken() : null;
  const token = authDetails || tokenFromCookie;
  
  let userType = null;
  if (token) {
    try {
      userType = getPrimaryRole(decode(token)?.authorities);
    } catch (error) {
      console.error('Token decode error:', error);
    }
  }

  return (
    <Route
      {...rest}
      render={(props) =>
        userType === ADMIN ? (
          <Component {...props} />
        ) : userType ? (
          <Redirect to={{ pathname: `/${country}` }} />
        ) : (
          // If no token found, show nothing to let auth initialize
          null
        )
      }
    />
  );
};

const AppRoutes = () => {
    const pathName = useSelector((state) => state?.router?.location?.pathname || '');

    const {path} = useRouteMatch();
    const {country} = useParams();

    

    return (
        <div>
            {pathName !== `/${country}` ? <NavBar isHomePage={false} /> : null}
            <Switch>
                <Route exact path={path} component={Home} />
                <Route path={`${path}/space`} component={ProfileAndBooking} />
                <Route path={`${path}/featured-space`} component={FeaturedSpaces} />
                <Route
                    path={`${path}/:city/:searchSlug(meeting-rooms|coworking-spaces|private-offices|virtual-offices|training-rooms|photo-shoot-studios)`}
                    component={SearchLandingRedirect}
                />
                <Route path={`${path}/:city?/spaces`} component={Result} />
                <Route path="/booking" component={Booking} />

                <Route path={`${path}/dashboard/myspaces`} >
                  <SpaceContainer/>
                </Route>
                <Route path={`${path}/dashboard/calendar`} component={CalendarWrapper} />
                <Route path={`${path}/dashboard/list-a-space`} component={SpaceListing} />
                <Route path={`${path}/dashboard/host-onboarding`} component={HostOnBoarding} />
                <Route path={`${path}/dashboard/visits`} component={ManageVisitsWrapper} />
                <Route path={`${path}/dashboard/user-management`} component={UserManagement} />

                <Route path={`${path}/message`} component={Message} />
                <Route path={`${path}/dialog`} component={Dialog} />
                <Route path={`${path}/loading`} component={loading} />

                <Route path={`${path}/dashboard/reports`} component={ReportContainer} />
                <Route path={`${path}/dashboard/host-reports`}>
                    <AdminDashboardLayout>
                        <HostReport />
                    </AdminDashboardLayout>
                </Route>
                <Route path={`${path}/dashboard/promotions`} component={PaidPromotions} />

                <AdminRoute path={`${path}/dashboard/admin`} component={AdminDashboard} />

                <AdminRoute path={`${path}/dashboard/admin`} component={AdminDashboard} />

                <Route path={`${path}/dashboard/mybooking`} key={'reservation'}>
                  <MyBookings componentName="mybookings" title={"My bookings"} />
                </Route>
                <Route path={`${path}/dashboard/reservations`}  key={'mybooking'}>
                  <MyBookings componentName="reservations" title={"Reservations"}/>
                </Route>

                <PrivateRoute path={`${path}/dashboard`} component={Dashboard} />
                <Route path={`${path}/about-us`} component={AboutUs} />
                <Route path={`${path}/help`} component={HelpAndSupport} />
                <Route path={`${path}/terms`} component={TermsAndConditions} />
                <Route path={`${path}/faq`} component={Faq} />
                <Route path={`${path}/blog/:slug`} component={BlogDetail} />
                <Route path={`${path}/blog`} component={Blog} />
                <Route path={`${path}/leave-a-review/:token`} component={ReviewPage} />
                <Route path={`${path}/forgotpassword/:forgotPasswordToken?`} component={ForgotPassword} />
                <Route path={`${path}/emailconfirmation/:verificationToken?`} component={EmailConfirmation} />
                <Route path={`${path}/image-enhance`} component={ImageEnhance} />
                </Switch>

        </div>
    )
};

export default AppRoutes;
